import { canv } from "@/canv/global.js";
import type { ExtendsOfElementBase } from "@/canvasController/types";
import type { DocumentViewerWidget } from "@/elements/documentViewer/DocumentViewer";
import type { DocumentTurnPageOption, widgetOption } from "@/widgetMenu/widgetMenu";
import { wmBase } from "@/widgetMenu/wmBase";

export class WmDocumentTurnPage extends wmBase {
    constructor(options: widgetOption<DocumentTurnPageOption>[]) {
        super();
        this.currentPage = options[0].value.currentPageNumber;
        this.totalPages = options[0].value.totalPages;
    }

    build() {
        return `
            <div class="wm_documentTurnPage">
                <div class="documentTurnPage" data-action='prev'>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 16L7 10L13 4" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                
                <div class="documentPageCountBlock">
                    <div class="documentCurrentPageInput">
                        <input 
                            type="number" 
                            inputMode="numeric"
                            pattern="\\d*"
                            step="1"
                            min="1" 
                            max="${this.totalPages}" 
                            value="${this.currentPage}" 
                            class="documentPageInputField"
                        />
                    </div>
                    <div class="documentDivider">/</div>
                    <div class="documentTotalPages">${this.totalPages}</div>
                </div>
                
                <div class="documentTurnPage" data-action='next'>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 16L13 10L7 4" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </div>
        `;
    }

    init() {
        const items = document.querySelectorAll<HTMLElement>(".documentTurnPage");
        items.forEach((item) => {
            item?.removeEventListener("click", this.itemClickTurnPage, false);
            item?.addEventListener("click", this.itemClickTurnPage, false);
        });

        const input = document.querySelector<HTMLInputElement>(".documentPageInputField");
        if (input) {
            input.addEventListener("input", this.handleInputChange);
            input.addEventListener("keydown", this.handleInputKeydown);
            input.addEventListener("focus", this.handleInputFocus);
            input.addEventListener("blur", this.handleInputBlur);
        }
    }

    handleInputKeydown = (e: KeyboardEvent): void => {
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            const input = e.currentTarget as HTMLInputElement;
            this.commitPageChange(input.value);
        }
        if (e.key === "e" || e.key === "E" || e.key === "." || e.key === "," || e.key === "-") {
            e.preventDefault();
        }
    };

    handleInputChange = (e: Event): void => {
        const input = e.currentTarget as HTMLInputElement;
        const pageNumber = Number.parseInt(input.value, 10);
        if (!Number.isNaN(pageNumber)) {
            this.currentPage = Math.min(Math.max(pageNumber, 1), this.totalPages);
        }
    };

    handleInputFocus = (e: FocusEvent): void => {
        canv.appInterface.inputFocused = 1;
        const input = e.currentTarget as HTMLInputElement;
        input.classList.add("active");
    };

    handleInputBlur = (e: FocusEvent): void => {
        canv.appInterface.inputFocused = 0;
        const input = e.currentTarget as HTMLInputElement;
        this.commitPageChange(input.value);
        input.classList.remove("active");
    };

    commitPageChange(value: string) {
        let pageNumber = Number.parseInt(value, 10);

        if (Number.isNaN(pageNumber)) {
            this.goToPage(this.currentPage);
            return;
        }

        if (pageNumber < 1) pageNumber = 1;
        if (pageNumber > this.totalPages) pageNumber = this.totalPages;

        this.goToPage(pageNumber);
    }

    itemClickTurnPage = (e: MouseEvent): void => {
        const target = e.currentTarget as HTMLElement;
        const actionType = target.dataset.action as "prev" | "next" | undefined;

        if (!actionType) return;

        const pageNumber = actionType === "next" ? this.currentPage + 1 : this.currentPage - 1;
        if (pageNumber < 1 || pageNumber > this.totalPages) return;
        this.goToPage(pageNumber);
    };

    goToPage(pageNumber: number) {
        const selectedEntities = canv.canvasController.selected;
        const isDocumentViewer = (obj: ExtendsOfElementBase): obj is DocumentViewerWidget =>
            obj?.constructorName === "documentViewer";

        if (selectedEntities.length === 1 && selectedEntities[0].isItemWrapper()) {
            const child = selectedEntities[0].onlyChild() as ExtendsOfElementBase | null;
            if (child && isDocumentViewer(child)) {
                child.goToPage(pageNumber);
                this.currentPage = pageNumber;

                const input = document.querySelector<HTMLInputElement>(".documentPageInputField");
                if (input) input.value = String(this.currentPage);
            }
        }
    }

    currentPage = 0;
    totalPages = 0;
}
